import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import type { Awareness } from 'y-protocols/awareness'

interface RemoteCursor {
  id: number
  name: string
  color: string
  left: number
  top: number
  height: number
}

/**
 * Renders each remote user's caret (with a name label) over the editor, driven by Yjs
 * awareness. Each client broadcasts its ProseMirror selection head (RichEditor); since
 * every doc is parsed from the same markdown, positions are comparable across clients.
 * Decorative (aria-hidden) — the accessible presence signal is the nav user count.
 */
export function CursorOverlay({ editor, awareness }: { editor: Editor; awareness: Awareness }) {
  const layerRef = useRef<HTMLDivElement>(null)
  const [cursors, setCursors] = useState<RemoteCursor[]>([])

  useEffect(() => {
    const recompute = (): void => {
      const layer = layerRef.current
      if (!layer) return
      const rect = layer.getBoundingClientRect()
      const size = editor.state.doc.content.size
      const next: RemoteCursor[] = []
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === awareness.clientID) return
        const user = (state as { user?: { name: string; color: string } }).user
        const cursor = (state as { cursor?: { head?: number } }).cursor
        if (!user || !cursor || typeof cursor.head !== 'number') return
        const pos = Math.max(0, Math.min(cursor.head, size))
        let coords: { left: number; top: number; bottom: number }
        try {
          coords = editor.view.coordsAtPos(pos)
        } catch {
          return
        }
        next.push({
          id: clientId,
          name: user.name,
          color: user.color || '#7a869a',
          left: coords.left - rect.left,
          top: coords.top - rect.top,
          height: coords.bottom - coords.top,
        })
      })
      setCursors(next)
    }

    recompute()
    awareness.on('change', recompute)
    editor.on('transaction', recompute)
    window.addEventListener('scroll', recompute, true) // capture: catch inner scroll containers
    window.addEventListener('resize', recompute)
    return () => {
      awareness.off('change', recompute)
      editor.off('transaction', recompute)
      window.removeEventListener('scroll', recompute, true)
      window.removeEventListener('resize', recompute)
    }
  }, [editor, awareness])

  return (
    <div className="cursor-layer" ref={layerRef} aria-hidden="true">
      {cursors.map((c) => (
        <span
          key={c.id}
          className="rcursor"
          style={{ left: c.left, top: c.top, height: c.height, background: c.color }}
        >
          <span className="rcursor-label" style={{ background: c.color }}>
            {c.name}
          </span>
        </span>
      ))}
    </div>
  )
}
